import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { API_BASE_URL } from '../config/api';
import { Calendar, MapPin, Ticket, ArrowRight, Users, Lightbulb, Compass, Handshake, Sparkles } from 'lucide-react';

const DIALOGUES = [
  { no: '01', title: 'Enterprise Resilience 2030', subtitle: 'Governance, Risk & Board Accountability', desc: 'How prepared are enterprises for the risks of the decade ahead? This Dialogue examines board responsibility, fiduciary oversight, enterprise risk governance, regulatory preparedness and the institutional capabilities required to build resilient organisations.', focus: ['Board accountability', 'Risk governance', 'Regulatory preparedness', 'Institutional resilience'] },
  { no: '02', title: 'Supply Chain Resilience', subtitle: 'Navigating a Fragmented Global Economy', desc: 'Global supply chains are being reshaped by trade volatility, geopolitical uncertainty and changing patterns of production. The Dialogue explores how enterprises can build greater resilience through diversification, strategic sourcing, logistics preparedness and better understanding of geopolitical risk.', focus: ['Trade volatility', 'Supply-chain diversification', 'Logistics', 'Geopolitical risk'] },
  { no: '03', title: 'ESG Beyond Compliance', subtitle: 'Strategic Advantage & Regulatory Preparedness', desc: 'ESG is increasingly moving from a reporting obligation to a strategic consideration. This Dialogue examines how organisations can anticipate regulatory evolution, integrate ESG into enterprise strategy and convert sustainability considerations into long-term value creation.', focus: ['ESG strategy', 'Regulatory preparedness', 'Value creation', 'Responsible enterprise'] },
  { no: '04', title: 'Stabilising MSMEs', subtitle: 'Governance, Capital & Institutional Discipline', desc: "MSMEs are central to India's economic resilience, yet many face challenges relating to capital, governance and scalability. The Dialogue explores practical approaches to risk mitigation, banking and capital alignment, institutional discipline and building enterprises capable of sustainable growth.", focus: ['MSME resilience', 'Capital access', 'Governance', 'Scalability'] },
  { no: '05', title: 'Climate Volatility as Enterprise Risk', subtitle: 'Adaptation Through the Lens of Governance', desc: 'Climate change is increasingly becoming an enterprise issue rather than an environmental issue alone. Floods, heat stress, energy risk and infrastructure vulnerability can directly affect business continuity, capital allocation and long-term competitiveness.', focus: ['Climate risk', 'Infrastructure resilience', 'Business continuity', 'Adaptation'] },
  { no: '06', title: 'AI, Automation & Institutional Risk Governance', subtitle: 'Innovating Without Compromising Integrity', desc: 'Artificial Intelligence and automation are transforming how organisations operate — while creating new dimensions of digital, cyber and institutional risk. The Dialogue examines responsible AI adoption, cyber and digital risk, accountability and the governance frameworks required.', focus: ['AI governance', 'Automation', 'Cyber risk', 'Responsible adoption'] },
  { no: '07', title: 'Regional Enterprise Ecosystems', subtitle: 'Distributed Growth & Economic Resilience', desc: "India's next phase of enterprise growth may increasingly extend beyond established metropolitan centres. This Dialogue explores the potential of Tier-2 and Tier-3 industrial ecosystems, regional enterprise clusters and de-urbanisation strategies.", focus: ['Regional ecosystems', 'Tier-2 & Tier-3 growth', 'Industrial clusters', 'Economic resilience'] },
  { no: '08', title: 'Corporate Strategy in an Era of Fiscal Tightening', subtitle: 'Balancing Ambition with Discipline', desc: 'Changing fiscal conditions and regulatory environments are reshaping corporate decision-making. The Dialogue examines tax risk, regulatory shifts, financial prudence and capital allocation — and how boards and management can balance growth ambitions with financial discipline.', focus: ['Fiscal environment', 'Tax risk', 'Capital allocation', 'Financial prudence'] },
  { no: '09', title: 'Circular Economy as Competitive Strategy', subtitle: 'Resource Efficiency as Competitiveness', desc: 'Resource efficiency is increasingly becoming a question of competitiveness. This Dialogue explores how circular economy principles can help enterprises improve cost stability, optimise resource utilisation and build stronger long-term competitive positions.', focus: ['Resource efficiency', 'Cost stability', 'Circularity', 'Competitiveness'] },
  { no: '10', title: 'CSR & Institutional Capital', subtitle: 'From Compliance to Measurable Impact', desc: 'CSR is evolving from an expenditure obligation towards a question of institutional accountability and measurable outcomes. The Dialogue examines monitoring and evaluation, fiduciary discipline, capital efficiency and outcome measurement.', focus: ['CSR governance', 'Impact measurement', 'Fiduciary discipline', 'Capital efficiency'] }
];

const BENEFITS = [
  { icon: Users, title: 'Curated Conversations', desc: 'Engagement with relevant enterprise, financial, governance and sector leaders.' },
  { icon: Compass, title: 'Multiple Perspectives', desc: 'Insights drawn from business, policy, governance and development perspectives.' },
  { icon: Lightbulb, title: 'Decision-Relevant Insights', desc: 'Discussion focused on issues that influence institutional choices and long-term strategy.' },
  { icon: Handshake, title: 'Meaningful Connections', desc: 'Build relationships with peers and stakeholders who share a serious interest in the subject.' }
];

export default function Events() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [announcements, setAnnouncements] = useState({});

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/events/`)
      .then(({ data }) => setEvents(data.filter(e => new Date(e.date) >= new Date())))
      .catch(() => {});
    axios.get(`${API_BASE_URL}/api/events/dialogues/all`)
      .then(({ data }) => {
        const map = {};
        data.dialogues.forEach(d => { map[d.no] = d; });
        setAnnouncements(map);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7]">
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1E3A5F] via-[#1E3A5F] to-[#2D5F8D] pt-32 pb-20 text-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[#E87722] font-semibold tracking-[0.25em] text-sm mb-4">CSED EVENTS · 2026–27 SERIES</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5" data-testid="events-page-title">Leadership Dialogues</h1>
          <p className="text-xl text-blue-100 mb-8">Conversations that matter. Perspectives that shape decisions.</p>
          <p className="text-blue-100/90 leading-relaxed max-w-3xl">
            CSED's Leadership Dialogues bring together enterprise leaders, financial decision-makers, governance
            professionals, policy thinkers and other relevant stakeholders to examine the forces shaping the future
            of business and society.
          </p>
        </div>
      </div>

      {/* Premise */}
      <div className="container mx-auto px-4 max-w-5xl -mt-10 relative z-10">
        <Card className="p-8 border-l-4 border-l-[#E87722] shadow-xl" data-testid="dialogue-premise-card">
          <p className="text-gray-600 mb-2 text-sm">The 2026–27 Dialogue Calendar has been designed around a simple premise:</p>
          <p className="text-xl font-semibold text-[#1E3A5F] italic mb-3">
            "The challenges confronting institutions today cannot be addressed in isolation."
          </p>
          <p className="text-gray-700 leading-relaxed">
            Economic stability, governance, social equity and environmental resilience are increasingly interconnected.
            Each Dialogue therefore seeks to move beyond conventional discussion and create a space for informed
            perspectives, meaningful exchange and actionable insights.
          </p>
        </Card>
      </div>

      <main className="flex-grow">
        {/* Open for Registration (bookable events from DB) */}
        {events.length > 0 && (
          <section className="container mx-auto px-4 max-w-5xl pt-14">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-6 flex items-center gap-2">
              <Ticket className="h-6 w-6 text-[#E87722]" />Open for Registration
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((ev) => (
                <Card key={ev.id} className="p-6 hover:shadow-xl transition-all" data-testid={`event-card-${ev.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-bold text-[#1E3A5F]">{ev.title}</h3>
                    <Badge variant="outline" className="capitalize text-xs">{ev.event_type}</Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{ev.description}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-2 mb-1">
                    <Calendar className="h-4 w-4 text-[#E87722]" />
                    {new Date(ev.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                  {ev.venue && <p className="text-sm text-gray-600 flex items-center gap-2 mb-2"><MapPin className="h-4 w-4 text-[#E87722]" />{ev.venue}</p>}
                  <p className="text-sm mb-2" data-testid={`event-price-${ev.id}`}>
                    <span className="font-bold text-[#1E3A5F]">₹{(ev.registration_fee / 100).toLocaleString('en-IN')}</span>
                    <span className="text-green-700"> · Members ₹{Math.round(ev.registration_fee * 0.75 / 100).toLocaleString('en-IN')} (25% off)</span>
                  </p>
                  <Button onClick={() => navigate(`/payment?category=event&item=${ev.id}`)}
                    className="w-full bg-[#E87722] hover:bg-[#d66a1a]" data-testid={`register-event-btn-${ev.id}`}>
                    Register & Pay
                  </Button>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Dialogue Series */}
        <section className="container mx-auto px-4 max-w-5xl py-14">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">The 2026–27 Dialogue Series</h2>
            <p className="text-sm text-gray-500">Dates and venues will be announced progressively</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {DIALOGUES.map((d) => (
              <Card key={d.no} className="p-6 hover:shadow-xl hover:-translate-y-0.5 transition-all border-t-4 border-t-[#1E3A5F]" data-testid={`dialogue-card-${d.no}`}>
                <div className="flex items-start gap-4">
                  <span className="text-4xl font-black text-[#E87722]/25 leading-none">{d.no}</span>
                  <div>
                    <h3 className="font-bold text-lg text-[#1E3A5F] leading-snug">{d.title}</h3>
                    <p className="text-sm font-medium text-[#E87722] mt-0.5">{d.subtitle}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mt-4">{d.desc}</p>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {d.focus.map((f, i) => (
                    <span key={i} className="text-[11px] px-2.5 py-1 rounded-full bg-[#1E3A5F]/5 text-[#1E3A5F] border border-[#1E3A5F]/10">{f}</span>
                  ))}
                </div>

                {/* Announcement / Registration / Interest */}
                {(() => {
                  const a = announcements[d.no];
                  return (
                    <div className="mt-4 pt-4 border-t">
                      {a?.date && (
                        <p className="text-sm text-gray-700 flex items-center gap-2 mb-3" data-testid={`dialogue-announced-${d.no}`}>
                          <Calendar className="h-4 w-4 text-[#E87722]" />
                          {new Date(a.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                          {a.venue && <span className="text-gray-500">• {a.venue}</span>}
                        </p>
                      )}
                      {a?.open_registration && a?.event_id ? (
                        <>
                          {a.early_bird_until && new Date(a.early_bird_until) > new Date() && (
                            <p className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1.5 mb-2" data-testid={`dialogue-earlybird-${d.no}`}>
                              Members-only early access until {new Date(a.early_bird_until).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} — public registration opens after
                            </p>
                          )}
                          {a.registration_fee ? (
                            <p className="text-sm mb-2" data-testid={`dialogue-price-${d.no}`}>
                              <span className="font-bold text-[#1E3A5F]">₹{Number(a.registration_fee).toLocaleString('en-IN')}</span>
                              <span className="text-green-700"> · Members ₹{Math.round(a.registration_fee * 0.75).toLocaleString('en-IN')} (25% off)</span>
                            </p>
                          ) : null}
                          <Button size="sm" onClick={() => navigate(`/payment?category=event&item=${a.event_id}`)}
                            className="bg-[#E87722] hover:bg-[#d66a1a] w-full" data-testid={`dialogue-register-btn-${d.no}`}>
                            Register & Pay
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" variant="outline"
                          onClick={() => navigate(`/engage?dialogue=${d.no}&interest=${encodeURIComponent(`Leadership Dialogue ${d.no} — ${d.title}`)}`)}
                          className="w-full border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
                          data-testid={`dialogue-interest-btn-${d.no}`}>
                          Register Interest
                        </Button>
                      )}
                    </div>
                  );
                })()}
              </Card>
            ))}
          </div>
        </section>

        {/* Flagship Summit */}
        <section className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5F8D] py-16 text-white">
          <div className="container mx-auto px-4 max-w-5xl" data-testid="flagship-summit-section">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-[#E87722]" />
              <p className="text-[#E87722] font-semibold tracking-[0.25em] text-sm">FLAGSHIP SUMMIT</p>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-2">Responsible Growth 2035</h2>
            <p className="text-lg text-blue-200 mb-6">Integrating Enterprise, Society & Climate</p>
            <div className="grid md:grid-cols-2 gap-8 items-start">
              <p className="text-blue-100/90 leading-relaxed">
                The Leadership Dialogue Series culminates in a flagship Summit bringing together the perspectives
                emerging across the year's conversations. Responsible Growth 2035 will explore the intersection of
                economic stability, social equity and environmental resilience, while looking beyond immediate
                challenges towards the choices that will shape the next decade.
              </p>
              <div>
                <p className="text-blue-100/90 leading-relaxed mb-4">
                  The Summit will seek to synthesise the insights, questions and possibilities emerging from the
                  Dialogue Series and translate them into a forward-looking conversation on responsible growth
                  and institutional leadership.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {['Economic stability', 'Social equity', 'Environmental resilience', 'Responsible growth', 'The decade ahead'].map((f, i) => (
                    <span key={i} className="text-[11px] px-2.5 py-1 rounded-full bg-white/10 border border-white/20">{f}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Participate */}
        <section className="container mx-auto px-4 max-w-5xl py-16">
          <h2 className="text-3xl font-bold text-[#1E3A5F] mb-2">Why Participate?</h2>
          <p className="text-gray-600 mb-8">CSED Leadership Dialogues are designed for <strong>substance over scale</strong>. Participants can expect:</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map((b, i) => (
              <Card key={i} className="p-6 text-center hover:shadow-xl transition-shadow" data-testid={`benefit-card-${i}`}>
                <div className="mx-auto h-12 w-12 rounded-full bg-[#E87722]/10 flex items-center justify-center mb-4">
                  <b.icon className="h-6 w-6 text-[#E87722]" />
                </div>
                <h3 className="font-bold text-[#1E3A5F] mb-2">{b.title}</h3>
                <p className="text-sm text-gray-600">{b.desc}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* Participation & Partnership */}
        <section className="bg-gradient-to-br from-orange-50 to-amber-50 border-y border-orange-100 py-16">
          <div className="container mx-auto px-4 max-w-5xl" data-testid="partnership-section">
            <h2 className="text-3xl font-bold text-[#1E3A5F] mb-6">Participation & Partnership</h2>
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <p className="text-gray-700 leading-relaxed">
                CSED Leadership Dialogues are designed as <strong>curated, paid-participation programmes</strong>,
                enabling participants to engage in high-quality conversations with a relevant peer group.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Organisations whose interests, expertise or solutions are relevant to the Dialogue themes may also
                participate as <strong>Strategic, Knowledge, Session or Institutional Partners</strong> — engaging
                meaningfully with a curated audience while supporting CSED's institutional mission.
              </p>
            </div>
            <p className="text-gray-600 mb-5">Participation and partnership opportunities will be opened progressively for each Dialogue.</p>
            <div className="flex flex-wrap gap-4">
              <Button onClick={() => navigate('/engage?type=event_dialogue')} className="bg-[#E87722] hover:bg-[#d66a1a]" data-testid="register-interest-btn">
                Register your Interest<ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button variant="outline" onClick={() => navigate('/engage?type=sponsorship')}
                className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white" data-testid="partnership-btn">
                Explore Partnership Opportunities<ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* Closing */}
        <section className="container mx-auto px-4 max-w-4xl py-16 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1E3A5F] mb-4">Building a Culture of Responsible Leadership</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            The CSED Leadership Dialogue Series is not intended to be a conventional event calendar. It is an evolving
            platform for serious conversations on the choices that will shape enterprises, communities and institutions
            in the years ahead.
          </p>
          <p className="text-sm text-gray-500 italic">
            CSED — Chamber for Socio Economic Development · Building conversations that contribute to more resilient
            enterprises, equitable societies and sustainable futures.
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
}
